import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarClock,
  Camera,
  Car,
  Check,
  ChevronRight,
  Handshake,
  Landmark,
  Layers,
  Palette,
  ShieldCheck,
  Wallet,
  X,
} from "lucide-react";
import { vehicleWhatsappHref } from "../data/site";
import { nameOf, type Vehicle } from "../data/vehicles";
import { useSimilarQuery, useVehicleQuery } from "../hooks/useVehicleQueries";
import { useGsapContext } from "../hooks/useGsapReveal";
import { detailIntro } from "../animations/gsapAnimations";
import { pkr, shortPrice } from "../utils/format";
import { assurancesFor } from "../utils/assurance";
import {
  purchaseOptionsFor,
  tenureOf,
  type PurchaseRoute,
} from "../utils/purchase";
import PurchaseRoutePicker from "../components/vehicle/PurchaseRoutePicker";
import Button from "../components/ui/Button";
import Section from "../components/ui/Section";
import SectionHeading from "../components/ui/SectionHeading";
import Skeleton from "../components/ui/Skeleton";
import LeaseCalculator from "../components/finance/LeaseCalculator";
import BookingModal from "../components/booking/BookingModal";
import TestDriveModal from "../components/vehicle/TestDriveModal";
import WhatsAppIcon from "../components/ui/WhatsAppIcon";
import VehicleGallery from "../components/vehicle/VehicleGallery";
import StudioStage, {
  STAGE_SUBJECT,
  StudioSpill,
} from "../components/vehicle/StudioStage";
import { CarSilhouette } from "../components/vehicle/VehicleMedia";
import ColourPicker from "../components/vehicle/ColourPicker";
import VehicleGrid from "../components/vehicle/VehicleGrid";
import NotFound from "./NotFound";

/* The car's page, built as a showroom stage.

   The photographs take the whole left of the screen under a headline the
   size of the hero's, and the deal rides beside them in one glass panel
   that stays put while the rest scrolls — so the price and the two ways to
   act on it are never further away than the right edge. Under the stage a
   ribbon of facts, then what the price covers as a grid of tiles, then the
   calculator seeded with this car and the cars standing next to it. */

/* Where the car stands. A sold car keeps its page — a shared link should
   not 404 — but nothing on it offers to book. */
const STATUS: Record<Vehicle["status"], { label: string; dot: string }> = {
  "in-stock": {
    label: "In stock",
    dot: "bg-mint shadow-[0_0_10px_rgb(37_211_102/0.7)]",
  },
  reserved: {
    label: "Reserved",
    dot: "bg-lime shadow-[0_0_10px_rgb(200_245_66/0.7)]",
  },
  sold: { label: "Sold", dot: "bg-dim" },
};

/* The frame a car nobody has photographed yet stands in: the same room a
   photographed car stands in, with the silhouette the cards wear standing
   on the floor at full size. It reads as a car waiting for its shoot,
   not as the hole where a picture should be. */
function AwaitingShoot({ vehicle }: { vehicle: Vehicle }) {
  return (
    <div className="group relative aspect-16/10 overflow-hidden rounded-[28px] border border-line shadow-lift">
      <StudioStage uid={`stage-${vehicle.id}`} />

      {/* Inside the ring, on the same box a photograph stands in, so a car
          waiting for its shoot is framed by the arc exactly as a
          photographed one is. */}
      <div className={`${STAGE_SUBJECT} [&>svg]:w-full [&>svg]:max-w-none`}>
        <CarSilhouette uid={`stage-${vehicle.id}`} />
      </div>

      <StudioSpill />

      <span className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-void/50 px-3.5 py-1.5 label-sm text-white backdrop-blur-md">
        <Camera
          aria-hidden="true"
          strokeWidth={1.8}
          className="size-3.5 text-lime"
        />
        Photography on its way
      </span>
    </div>
  );
}

function VehicleDetails() {
  const { id = "" } = useParams<{ id: string }>();

  const { data: vehicle, isPending: loading } = useVehicleQuery(id);
  const { data: similar = [] } = useSimilarQuery(id, 3);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [testDriveOpen, setTestDriveOpen] = useState(false);
  /* Which route the panel is quoting the first payment on. On spot leads:
     the car on this page is one standing on the floor. */
  const [route, setRoute] = useState<PurchaseRoute>("on-spot");

  /* The paints this car is offered in — its own, and every other colour the
     same trim and model year is standing on the floor in. Picking one says
     which paint the car is wanted in and nothing else: the page goes on
     quoting this car, and the choice is read again on the booking form. */
  /* Kept with the car it was made on: this component is reused when one car
     opens another, and the last car's pick is not this car's. */
  const [picked, setPicked] = useState<{
    vehicleId: string;
    colourId: string;
  } | null>(null);
  const colours = vehicle?.colours ?? [];
  const own =
    colours.find((option) => option.id === vehicle?.id) ?? colours[0] ?? null;
  const colour =
    (picked && picked.vehicleId === vehicle?.id
      ? colours.find((option) => option.id === picked.colourId)
      : null) ?? own;

  /* The frames the gallery shows. They do not change with the paint: a car is
     photographed once for its trim, and those frames stand for every colour
     of it on the floor.

     Memoised because the gallery resets its position whenever the array it is
     handed changes, and a fresh array on every render would mean a gallery
     that never leaves frame one. */
  const frames = useMemo(() => vehicle?.images ?? [], [vehicle]);

  /* The page arrives in the order somebody looks at a car — the way back
     and the name, the photographs, the money, the facts — and the sections
     below reveal as they are reached. Keyed on the id because nothing is
     mounted until the vehicle resolves, and because navigating between two
     cars reuses this component rather than remounting it. */
  const pageRef = useGsapContext<HTMLDivElement>(
    (scope) => detailIntro(scope),
    [vehicle?.id],
  );

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1320px] px-5 pt-[112px] pb-24 sm:px-8 sm:pt-[128px]">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="mt-8 h-3 w-24" />
        <Skeleton className="mt-4 h-14 w-2/5" />
        <Skeleton className="mt-3 h-4 w-1/3" />
        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_400px]">
          <div>
            <Skeleton className="aspect-16/10 w-full rounded-[28px]" />
            <Skeleton className="mt-5 h-20 w-full rounded-2xl" />
          </div>
          <Skeleton className="h-[440px] w-full rounded-[28px]" />
        </div>
      </div>
    );
  }

  /* An unknown id is a 404, not an empty page — the same one a bad URL gets. */
  if (!vehicle) return <NotFound />;

  const title = nameOf(vehicle);
  const status = STATUS[vehicle.status] ?? STATUS["in-stock"];
  const sold = vehicle.status === "sold";
  const reserved = vehicle.status === "reserved";
  const bookingAvailable =
    vehicle.status === "in-stock" && vehicle.availability !== "on-spot";

  /* Zero is the API's "nobody has priced this yet", not a free car. */
  const priced = vehicle.price > 0;
  const monthly = vehicle.instalment ?? 0;
  const leased = vehicle.financingAvailable && priced && monthly > 0;
  /* The ways this car can be taken: on the spot, or on booking. Same price
     and same monthly either way — what differs is the payment made up front,
     which is why they sit under the monthly rather than beside the price.
     Most of the floor is offered both ways and the panel makes it a choice;
     a car the showroom will only book, or only sell off the floor, comes
     back as the one route and is captioned rather than picked. A sold car
     has nothing left to choose about. */
  const routes = purchaseOptionsFor(vehicle);
  const choice = !sold;
  /* The deposit that monthly figure was worked out from — this car's own when
     the showroom set terms for it, thirty per cent of the price otherwise.
     Both come resolved from the API, so the page never has to know which. */
  const deposit = vehicle.deposit;
  const years = tenureOf(vehicle);

  const assurances = assurancesFor(vehicle);
  const covered = assurances.filter((item) => item.included).length;

  const facts = [
    { icon: Car, term: "Model", value: vehicle.model },
    /* The trim, beside the model rather than folded into it: "Grande X 1.8"
       is what tells this Corolla from the Corolla next to it, and it is the
       first thing anybody asks after the model itself. */
    ...(vehicle.variant
      ? [{ icon: Layers, term: "Variant", value: vehicle.variant }]
      : []),
    {
      icon: CalendarClock,
      term: "Model year",
      value: vehicle.year > 0 ? String(vehicle.year) : "On request",
    },
    /* Only on a car offered in one paint. On any other the picker under the
       stage is already showing the name, and a fact repeating it would go
       stale the moment a swatch is clicked. */
    ...(colours.length === 1
      ? [
          {
            icon: Palette,
            term: "Colour",
            value: colours[0].name,
            swatch: colours[0].hex,
          },
        ]
      : []),
    {
      icon: Landmark,
      term: "Finance",
      value: vehicle.financingAvailable
        ? "Bank lease available"
        : "Cash purchase only",
    },
    /* Only on a car that is not offered both ways. On one that is, the panel
       above is already offering the choice, and a fact repeating it would be
       a row saying what the control beside it says. */
    ...(routes.length === 1
      ? [
          {
            icon: Handshake,
            term: "Availability",
            value: `${routes[0].label} only`,
          },
        ]
      : []),
  ];

  /* The same message the card's button sends — one composer, so the desk
     receives an identically shaped enquiry whichever surface it came from.
     From here it also carries the paint being looked at. */
  const enquiry = vehicleWhatsappHref(vehicle, colour?.name);

  /* The deal, as one glass panel. Built here rather than as a component of
     its own because every line of it reads this car's state — the price, the
     status, the paint, the two dialogs — and threading twelve props through
     would say less than the panel itself does. */
  const dealPanel = (
    <div className="relative overflow-hidden rounded-[28px] border border-line bg-raised/80 p-6 shadow-pop backdrop-blur-xl sm:p-7">
      {/* Lime light caught along the top edge of the glass. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(70%_100%_at_50%_0%,rgb(200_245_66/0.12),transparent)]"
      />

      <div className="relative">
        {/* ---------- the money ---------- */}
        <div className="flex items-start justify-between gap-4">
          <span className="label-sm text-dim">Cash price</span>
          <span className="rounded-full border border-line px-2.5 py-1 text-[10.5px] font-medium tracking-[0.06em] text-steel uppercase">
            Ex-showroom
          </span>
        </div>

        {priced ? (
          <>
            <strong className="mt-2 flex items-baseline gap-2 font-display text-[clamp(2rem,3.4vw,2.5rem)] leading-none font-semibold tracking-[-0.04em] text-platinum tabular-nums">
              <span className="text-[15px] font-medium tracking-normal text-dim">
                PKR
              </span>
              {pkr.format(vehicle.price)}
            </strong>
            <span className="mt-2 block text-[12.5px] text-dim">
              {shortPrice(vehicle.price)}
            </span>
          </>
        ) : (
          <strong className="mt-2 block font-display text-[28px] leading-none font-semibold tracking-[-0.03em] text-platinum">
            Price on request
          </strong>
        )}

        {leased ? (
          <div className="mt-6 rounded-2xl border border-lime/25 bg-lime/[0.07] px-5 py-4">
            <span className="label-sm text-lime">Monthly from</span>
            <strong className="mt-1.5 flex items-baseline gap-1.5 font-display text-[30px] leading-none font-semibold tracking-[-0.03em] text-lime tabular-nums">
              {pkr.format(monthly)}
              <span className="text-[13px] font-medium text-lime/70">/mo</span>
            </strong>
            <p className="mt-2.5 text-[12.5px] leading-relaxed text-steel tabular-nums">
              {choice || deposit === null
                ? `Over ${years} years through our partner banks.`
                : `After ${shortPrice(deposit)} down, over ${years} years through our partner banks.`}
            </p>
          </div>
        ) : !vehicle.financingAvailable ? (
          <p className="mt-6 rounded-2xl border border-line bg-surface/60 px-5 py-4 text-[13px] leading-relaxed text-steel">
            Imported to order — available for cash purchase, not on lease.
          </p>
        ) : null}

        {/* ---------- how it is being taken ----------
            The price above is the car's; this is the visitor's half of the
            deal, and the only figure on the panel that answers to a control. */}
        {choice ? (
          <div className="mt-6 rounded-2xl border border-line bg-surface/60 px-5 py-4">
            <PurchaseRoutePicker
              options={routes}
              value={route}
              onChange={setRoute}
              name={title}
              size="lg"
            />
          </div>
        ) : null}

        {/* ---------- the actions ---------- */}
        {sold ? (
          <div className="mt-6 flex flex-col gap-3">
            <p className="rounded-2xl border border-line bg-surface/60 px-5 py-4 text-[13.5px] leading-relaxed text-steel">
              This one has found its owner. The cars beside it below are at the
              same level — or ask us what is arriving next.
            </p>
            <Button to="/home#cars" size="lg" arrow className="w-full">
              See what is on the floor
            </Button>
            <Button
              variant="ghost"
              size="lg"
              href={enquiry}
              target="_blank"
              rel="noreferrer"
              className="w-full border-mint/45 bg-mint/10 text-mint-deep hover:border-mint hover:bg-mint/20"
            >
              <WhatsAppIcon className="size-4" />
              Ask about the next one
            </Button>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-3">
            {reserved ? (
              <p className="rounded-2xl border border-lime/20 bg-lime/[0.05] px-5 py-3.5 text-[13px] leading-relaxed text-steel">
                A booking is being finalised on this car. Ask to be next in line
                — reservations do fall through.
              </p>
            ) : null}

            {leased ? (
              <Button to="/financing" size="lg" arrow className="w-full">
                Get financing
              </Button>
            ) : null}

            {/* The card's two actions, at full width: file the booking,
                  or ask about the car. Same modal, same composed message. */}
            <div className={`grid gap-3 ${bookingAvailable ? "sm:grid-cols-2" : ""}`}>
              {bookingAvailable ? (
                <Button
                  size="lg"
                  variant={leased ? "ghost" : "primary"}
                  onClick={() => setBookingOpen(true)}
                >
                  <Wallet aria-hidden="true" strokeWidth={2} className="size-4" />
                  Book now
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="lg"
                href={enquiry}
                target="_blank"
                rel="noreferrer"
                className="border-mint/45 bg-mint/10 text-mint-deep hover:border-mint hover:bg-mint/20"
              >
                <WhatsAppIcon className="size-4" />
                {vehicle.availability === "on-spot" && !reserved
                  ? "Enquire on spot"
                  : "Enquire on WhatsApp"}
              </Button>
            </div>

            {/* Not everybody buying is ready to transfer a token today.
                  Quieter than the two above, and deliberately so — but
                  still here, because it is the step some visitors need
                  first. */}
            <button
              type="button"
              onClick={() => setTestDriveOpen(true)}
              className="group/td mt-1 inline-flex items-center justify-center gap-2 text-[13px] text-steel transition-colors hover:text-platinum"
            >
              <CalendarClock
                aria-hidden="true"
                strokeWidth={1.7}
                className="size-4"
              />
              Or book a test drive first
              <ArrowUpRight
                aria-hidden="true"
                strokeWidth={1.8}
                className="size-3.5 text-lime transition-transform group-hover/td:translate-x-0.5 group-hover/td:-translate-y-0.5"
              />
            </button>
          </div>
        )}

        {/* ---------- house policy, under the fold of the panel ---------- */}
        <ul className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line/70">
          {[
            { icon: ShieldCheck, text: "No hidden charges" },
            { icon: Car, text: "Delivery on the spot" },
          ].map((item) => (
            <li
              key={item.text}
              className="flex items-center gap-2.5 bg-surface/70 px-3.5 py-3 text-[12px] font-medium text-silver"
            >
              <item.icon
                aria-hidden="true"
                strokeWidth={1.7}
                className="size-4 shrink-0 text-lime"
              />
              {item.text}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <>
      <div ref={pageRef} className="relative overflow-x-clip">
        {/* ---------- ambient light ----------
            Lime falling into the black from the top right, behind the deal
            panel, the way the hero lights its badge. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
        >
          <div className="absolute top-[-8%] right-[-10%] h-[520px] w-[680px] rounded-full bg-[radial-gradient(closest-side,rgb(200_245_66/0.1),transparent)]" />
          <div className="absolute top-[30%] left-[-14%] h-[420px] w-[560px] rounded-full bg-[radial-gradient(closest-side,rgb(200_245_66/0.05),transparent)]" />
        </div>

        <div className="mx-auto w-full max-w-[1320px] px-5 pt-[112px] pb-20 sm:px-8 sm:pt-[128px]">
          {/* ---------- the way back, and where the car stands ---------- */}
          <div
            data-detail="crumb"
            className="flex flex-wrap items-center justify-between gap-4"
          >
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-[13px] text-steel"
            >
              <Link
                to="/home#cars"
                className="inline-flex items-center gap-2 rounded-full border border-line bg-raised/80 px-3.5 py-1.5 backdrop-blur-md transition hover:border-lime/50 hover:text-platinum"
              >
                <ArrowLeft
                  aria-hidden="true"
                  strokeWidth={1.8}
                  className="size-3.5"
                />
                All vehicles
              </Link>
              <ChevronRight
                aria-hidden="true"
                strokeWidth={1.6}
                className="size-3.5 text-dim"
              />
              <span>{vehicle.brand}</span>
              <ChevronRight
                aria-hidden="true"
                strokeWidth={1.6}
                className="size-3.5 text-dim"
              />
              <span aria-current="page" className="text-platinum">
                {vehicle.model}
              </span>
            </nav>

            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex h-9 items-center gap-2 rounded-full border border-line bg-raised/80 px-3.5 text-[12px] font-medium text-platinum backdrop-blur-md">
                <span
                  aria-hidden="true"
                  className={`size-1.5 rounded-full ${status.dot}`}
                />
                {status.label}
              </span>
            </div>
          </div>

          {/* ---------- the name ---------- */}
          <header className="mt-10 sm:mt-12">
            <p data-detail="eyebrow" className="flex items-center gap-3 label text-lime">
              <span aria-hidden="true" className="h-px w-9 coachline" />
              {vehicle.brand}
              {vehicle.tag ? (
                <>
                  <span
                    aria-hidden="true"
                    className="size-1 rounded-full bg-lime/50"
                  />
                  {vehicle.tag}
                </>
              ) : null}
            </p>

            {/* One name, at display size. The trim is what the floor writes
                the car down as — "Honda City 1.2 Auto 2026" — and printing the
                model above it only said the same thing twice. A car entered
                without a trim still has its model to be called. */}
            <div className="mt-5">
              <h1
                data-detail="title"
                className="font-display text-[clamp(2.6rem,6vw,5.2rem)] leading-[0.98] font-bold tracking-[-0.035em] text-balance text-platinum"
              >
                {vehicle.variant || vehicle.model}
              </h1>
            </div>
          </header>

          {/* Three cells on two columns. The stage and the facts stack on the
              left; the deal takes the right and spans both rows, so it can
              stay pinned while the left scrolls. On a phone the three simply
              stack in source order — stage, deal, facts — so the price is
              never six tiles away from the photographs. */}
          <div className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-x-12 lg:gap-y-8 xl:grid-cols-[minmax(0,1fr)_420px]">
            {/* ================= the stage ================= */}
            <div data-detail="stage" className="min-w-0 lg:col-start-1 lg:row-start-1">
              {frames.length > 0 ? (
                <VehicleGallery images={frames} alt={title} />
              ) : (
                <AwaitingShoot vehicle={vehicle} />
              )}
            </div>

            {/* ================= the deal ================= */}
            <aside
              data-detail="deal"
              className="min-w-0 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:sticky lg:top-[104px] lg:self-start"
            >
              {dealPanel}
            </aside>

            {/* ================= the facts ================= */}
            <div className="flex min-w-0 flex-col gap-8 lg:col-start-1 lg:row-start-2">
              {/* ---------- the ribbon of facts ----------
                  One row, told apart by hairlines, each fact under a ringed
                  icon — the hero's assurance strip, spelled for a car. */}
              <dl
                data-detail="facts"
                className="flex flex-wrap gap-px overflow-hidden rounded-2xl border border-line bg-line/70 shadow-card"
              >
                {facts.map((fact) => (
                  <div
                    key={fact.term}
                    className="flex min-w-[46%] flex-1 items-center gap-3.5 bg-raised/90 px-4 py-4 backdrop-blur-md sm:min-w-[180px] sm:px-5"
                  >
                    <span
                      aria-hidden="true"
                      className="icon-ring size-10 shrink-0"
                    >
                      <fact.icon strokeWidth={1.6} className="size-4" />
                    </span>
                    <div className="min-w-0">
                      <dt className="label-sm text-dim">{fact.term}</dt>
                      <dd className="mt-1 flex items-center gap-2 text-[14px] leading-snug font-medium text-platinum">
                        {"swatch" in fact && fact.swatch ? (
                          <span
                            aria-hidden="true"
                            className="size-3 shrink-0 rounded-full ring-1 ring-white/20"
                            style={{ backgroundColor: fact.swatch }}
                          />
                        ) : null}
                        <span>{fact.value}</span>
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>

              {/* Directly under the frames it names. On a phone the stage is
                  the first thing on the page and this is the second, so the
                  swatch and its effect are never a scroll apart. */}
              <ColourPicker
                colours={colours}
                value={colour?.id ?? vehicle.id}
                onChange={(colourId) =>
                  setPicked({ vehicleId: vehicle.id, colourId })
                }
                className="rounded-2xl border border-line bg-raised/60 px-5 py-5"
              />

              {/* ---------- what the deal covers ----------
                  Same source as the card's checklist, spelled out: on the
                  card the labels have to carry themselves, here there is
                  room to say what each one means for the buyer. */}
              <section
                data-detail="section"
                aria-labelledby="assurance-title"
                className="pt-2"
              >
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-3 label text-lime">
                      <span aria-hidden="true" className="h-px w-9 coachline" />
                      The deal
                    </p>
                    <h2
                      id="assurance-title"
                      className="mt-3 font-display text-display-3 text-platinum"
                    >
                      What this price covers
                    </h2>
                  </div>
                  <p className="text-[13px] text-steel tabular-nums">
                    <span className="font-semibold text-platinum">
                      {covered}
                    </span>{" "}
                    of {assurances.length} included
                  </p>
                </div>

                <ul className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {assurances.map((item) => (
                    <li
                      key={item.key}
                      className={`flex flex-col gap-4 rounded-2xl border p-5 transition-colors duration-300 ${
                        item.included
                          ? "border-line bg-raised shadow-card hover:border-lime/30"
                          : "border-dashed border-line/80 bg-transparent"
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`grid size-10 place-items-center rounded-full ${
                          item.included
                            ? "border border-mint/35 bg-mint/10 text-mint-deep"
                            : "border border-line bg-surface text-dim"
                        }`}
                      >
                        {item.included ? (
                          <Check strokeWidth={2.4} className="size-4" />
                        ) : (
                          <X strokeWidth={2.2} className="size-4" />
                        )}
                      </span>
                      <div>
                        <p
                          className={`text-[14.5px] font-semibold tracking-[-0.01em] ${
                            item.included ? "text-platinum" : "text-steel"
                          }`}
                        >
                          {item.label}
                          <span className="sr-only">
                            {item.included ? ": yes" : ": not included"}
                          </span>
                        </p>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-dim">
                          {item.detail}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- calculator, seeded with this car ---------- */}
      {leased && !sold ? (
        <Section tone="surface" labelledBy="vehicle-calc-title">
          <SectionHeading
            id="vehicle-calc-title"
            eyebrow="Lease this car"
            title="Your monthly payment"
            lede={`Started from the ${title}'s price. Change the down payment or the tenure to see what moves.`}
          />
          {/* Rendered only once the vehicle has resolved, so the calculator
              seeds from the real price at mount rather than being reset. */}
          <LeaseCalculator
            variant="compact"
            initialPrice={vehicle.price}
            initialDownPayment={deposit ?? undefined}
            vehicleName={`${vehicle.year} ${title}`}
            className="mt-12"
          />
        </Section>
      ) : null}

      {/* ---------- similar ---------- */}
      {similar.length > 0 ? (
        <Section tone="ink" labelledBy="similar-title">
          <SectionHeading
            id="similar-title"
            eyebrow="Similar vehicles"
            title="Other cars at this level"
            action={
              <Button variant="ghost" to="/home#cars" arrow>
                All vehicles
              </Button>
            }
          />
          <VehicleGrid vehicles={similar} columns={3} className="mt-12" />
        </Section>
      ) : null}

      {/* ---------- sticky action bar, mobile only ---------- */}
      <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 md:hidden">
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-ink/85 px-4 py-3 shadow-pop backdrop-blur-xl">
          <div className="min-w-0 flex-1">
            <span className="block label-sm text-dim">
              {sold ? status.label : leased ? "From" : "Cash price"}
            </span>
            <strong className="block truncate font-display text-[17px] font-semibold text-platinum tabular-nums">
              {!priced
                ? "Price on request"
                : leased
                  ? `${pkr.format(monthly)} /mo`
                  : `PKR ${shortPrice(vehicle.price)}`}
            </strong>
          </div>

          {sold ? (
            <Button to="/home#cars" size="sm" className="shrink-0">
              See similar
            </Button>
          ) : bookingAvailable ? (
            <Button
              size="sm"
              className="shrink-0"
              onClick={() => setBookingOpen(true)}
            >
              Book now
            </Button>
          ) : (
            <Button
              size="sm"
              className="shrink-0"
              href={enquiry}
              target="_blank"
              rel="noreferrer"
            >
              Enquire
            </Button>
          )}
        </div>
      </div>

      {bookingAvailable ? (
        <BookingModal
          vehicle={vehicle}
          colour={colour ?? null}
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
        />
      ) : null}

      <TestDriveModal
        vehicle={vehicle}
        open={testDriveOpen}
        onClose={() => setTestDriveOpen(false)}
      />
    </>
  );
}

export default VehicleDetails;
